// FleetEnvironmentDetailPage.tsx -- central-hub fleet environment detail.
// Extends the basic license/heartbeat view with a Containers card showing
// the last-heartbeat snapshot. No tunnel required — data arrives via the
// existing push-based heartbeat channel.

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader, StatusBadge, Modal, btnPrimary, btnSecondary, btnDanger, colors } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { livenessFromLastSeen, relativeTime, type EnvironmentRow } from './fleet-helpers'
import { useAuth } from '../../hooks/use-auth'
import { TenanciesCard } from './TenanciesCard'
import type { ContainerStatus } from '@alpha-edge/shared'

interface DetailResponse {
  readonly environment: EnvironmentRow & { readonly ownerOrganizationName: string | null }
  readonly latestHeartbeat: {
    readonly id: string
    readonly receivedAt: string
    readonly version: string | null
    readonly syncLagSeconds: number | null
    readonly instrumentsActive: number | null
    readonly instrumentsTotal: number | null
    readonly containers?: readonly ContainerStatus[] | null
  } | null
}

interface RotateResponse {
  readonly plaintextKey: string
  readonly displayedOnce: true
}

const STALE_THRESHOLD_MS = 5 * 60 * 1000

function containerStatusBadge(status: string): 'active' | 'inactive' | 'warning' {
  if (status === 'running') return 'active'
  if (status === 'paused') return 'warning'
  return 'inactive'
}

function relativeUptime(started: string | null | undefined): string {
  if (!started) return '—'
  const ms = Date.now() - new Date(started).getTime()
  if (ms < 0) return '—'
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ${minutes % 60}m`
  const days = Math.floor(hours / 24)
  return `${days}d ${hours % 24}h`
}

export function FleetEnvironmentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<DetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rotatedKey, setRotatedKey] = useState<string | null>(null)
  const [rotating, setRotating] = useState(false)
  const [slaDraft, setSlaDraft] = useState<string>('')
  const [recentEvents, setRecentEvents] = useState<
    readonly {
      id: string
      source: string
      severity: string
      summary: string
      occurredAt: string
      details?: Record<string, unknown>
    }[]
  >([])
  const [recentErrors, setRecentErrors] = useState<
    readonly { id: string; ts: string; container: string; message: string }[]
  >([])
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null)
  const [savingTerms, setSavingTerms] = useState(false)
  const [togglingEdgeReport, setTogglingEdgeReport] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [retainSecrets, setRetainSecrets] = useState(true)
  const [archiving, setArchiving] = useState(false)
  const { user } = useAuth()
  const isMedflow = user?.role === 'medflow_admin'

  useEffect(() => {
    if (!id) return
    let cancelled = false
    api
      .get<DetailResponse>(`/fleet/environments/${id}`)
      .then((res) => {
        if (cancelled) return
        setDetail(res.data ?? null)
        if (res.data) setSlaDraft(String(res.data.environment.analyticsDeletionSlaDays))
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load environment')
        setLoading(false)
      })
    api
      .get<
        readonly {
          id: string
          source: string
          severity: string
          summary: string
          occurredAt: string
          details?: Record<string, unknown>
        }[]
      >(`/admin/operational-events?environmentId=${id}&limit=25`)
      .then((res) => {
        if (!cancelled) setRecentEvents(res.data ?? [])
      })
      .catch(() => {})
    api
      .get<readonly { id: string; ts: string; container: string; message: string }[]>(
        `/admin/fleet/logs?environmentIds=${id}&level=error&limit=25`,
      )
      .then((res) => {
        if (!cancelled) setRecentErrors(res.data ?? [])
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [id])

  async function rotateKey(): Promise<void> {
    if (!id) return
    if (!confirm('Rotate the enrollment key? The old key will stop working immediately.')) return
    setRotating(true)
    try {
      const res = await api.post<RotateResponse>(`/fleet/environments/${id}/rotate-key`, {})
      if (res.data) setRotatedKey(res.data.plaintextKey)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Rotate failed')
    } finally {
      setRotating(false)
    }
  }

  async function saveTerms(): Promise<void> {
    if (!id || !detail) return
    const days = Number(slaDraft)
    if (!Number.isInteger(days) || days < 1 || days > 3650) {
      setError('SLA must be an integer between 1 and 3650 days')
      return
    }
    setSavingTerms(true)
    try {
      await api.patch(`/fleet/environments/${id}/terms`, { analyticsDeletionSlaDays: days })
      setDetail({
        ...detail,
        environment: { ...detail.environment, analyticsDeletionSlaDays: days },
      })
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSavingTerms(false)
    }
  }

  async function toggleExpectsEdgeReport(): Promise<void> {
    if (!id || !detail) return
    const next = !detail.environment.expectsEdgeReport
    const message = next
      ? 'Resume edge-derived alerts for this environment? Only flip on once an AlphaEdge edge stack is actually reporting in.'
      : 'Suppress edge-derived alerts (stale_deployment, container health, log bursts) for this environment? Use for instrument-monitoring-only envs with no AlphaEdge edge stack.'
    if (!confirm(message)) return
    setTogglingEdgeReport(true)
    try {
      await api.patch(`/fleet/environments/${id}/terms`, { expectsEdgeReport: next })
      setDetail({
        ...detail,
        environment: { ...detail.environment, expectsEdgeReport: next },
      })
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Toggle failed')
    } finally {
      setTogglingEdgeReport(false)
    }
  }

  async function handleArchive(): Promise<void> {
    if (!id) return
    setArchiving(true)
    try {
      await api.post(`/fleet/environments/${id}/archive`, { retainSecrets })
      navigate('/fleet')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Archive failed')
      setArchiving(false)
      setShowArchiveModal(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>
  if (error) return <div style={{ color: colors.danger }}>{error}</div>
  if (!detail) return <div>Environment not found.</div>

  const env = detail.environment
  const liveness = livenessFromLastSeen(env.lastSeenAt)
  const heartbeat = detail.latestHeartbeat
  const containers = heartbeat?.containers ?? null

  // Determine if the heartbeat data is stale (>5 minutes old)
  const heartbeatAgeMs = heartbeat ? Date.now() - new Date(heartbeat.receivedAt).getTime() : null
  const isContainerDataStale = heartbeatAgeMs !== null && heartbeatAgeMs > STALE_THRESHOLD_MS

  return (
    <div>
      <PageHeader
        title={env.name}
        subtitle={env.ownerOrganizationName ?? ''}
        backTo="/fleet"
        backLabel="Back to Fleet"
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={rotateKey} disabled={rotating}>
              {rotating ? 'Rotating...' : 'Rotate key'}
            </button>
            <button type="button" onClick={() => setShowArchiveModal(true)}>
              Archive
            </button>
          </div>
        }
      />

      {rotatedKey && (
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            color: '#92400e',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
          }}
        >
          <div style={{ marginBottom: '0.5rem' }}>
            <strong>New enrollment key.</strong> Shown exactly once. Paste it into the deployment and click Done.
          </div>
          <code style={{ display: 'block', wordBreak: 'break-all', padding: '0.5rem', background: '#fff' }}>
            {rotatedKey}
          </code>
        </div>
      )}

      {/* Environment metadata grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1rem',
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '1.25rem',
          maxWidth: '900px',
          marginBottom: '1rem',
        }}
      >
        <Field label="Liveness">
          <StatusBadge status={liveness} />
        </Field>
        <Field label="Environment type">{env.environmentType}</Field>
        <Field label="Version">{env.currentVersion ?? '—'}</Field>
        <Field label="Last seen">{relativeTime(env.lastSeenAt)}</Field>
        <Field label="Enrolled">{new Date(env.enrolledAt).toLocaleString()}</Field>
        <Field label="Analytics deletion SLA">
          {isMedflow ? (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="number"
                min={1}
                max={3650}
                value={slaDraft}
                onChange={(e) => setSlaDraft(e.target.value)}
                style={{ width: '6rem' }}
                aria-label="Analytics deletion SLA in days"
              />
              <span style={{ color: colors.textMuted }}>days</span>
              <button
                type="button"
                onClick={saveTerms}
                disabled={savingTerms || slaDraft === String(env.analyticsDeletionSlaDays)}
                style={btnPrimary}
              >
                {savingTerms ? 'Saving…' : 'Save'}
              </button>
            </div>
          ) : (
            <>{env.analyticsDeletionSlaDays} days</>
          )}
        </Field>
        <Field label="Edge stack reporting">
          {isMedflow ? (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span data-testid="expects-edge-report-state">
                {env.expectsEdgeReport ? 'Expected' : 'Split mode (suppressed)'}
              </span>
              <button
                type="button"
                onClick={toggleExpectsEdgeReport}
                disabled={togglingEdgeReport}
                style={btnPrimary}
                data-testid="toggle-expects-edge-report"
              >
                {togglingEdgeReport ? 'Saving…' : env.expectsEdgeReport ? 'Switch to split mode' : 'Resume edge alerts'}
              </button>
            </div>
          ) : (
            <>{env.expectsEdgeReport ? 'Expected' : 'Split mode (suppressed)'}</>
          )}
        </Field>
        <Field label="Latest heartbeat">
          {heartbeat ? (
            <>
              {relativeTime(heartbeat.receivedAt)} · {heartbeat.instrumentsActive ?? 0}/
              {heartbeat.instrumentsTotal ?? 0} instruments
            </>
          ) : (
            'none yet'
          )}
        </Field>
      </div>

      {/* Tenancy / license management (medflow_admin). Licensing is
          per-tenancy now -- the env hosts 1..N tenancies, each with its
          own license key + status/tier/expiry/features. */}
      {isMedflow && id && <TenanciesCard environmentId={id} defaultOrganizationId={env.ownerOrganizationId} />}

      {/* Containers card */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '1.25rem',
          maxWidth: '900px',
          marginBottom: '1rem',
        }}
      >
        <div
          style={{
            fontSize: '0.8125rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: colors.textMuted,
            marginBottom: '0.75rem',
          }}
        >
          Containers
        </div>

        {isContainerDataStale && (
          <div
            style={{
              marginBottom: '12px',
              padding: '8px 12px',
              background: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: '4px',
              color: '#92400e',
              fontSize: '0.8125rem',
            }}
          >
            Stale data — last heartbeat received {relativeTime(heartbeat?.receivedAt ?? null)} ago. Container status may
            not reflect current state.
          </div>
        )}

        {!heartbeat || containers == null ? (
          <p style={{ color: colors.textMuted, fontSize: '0.875rem' }}>
            No container data in the latest heartbeat. The edge deployment may be running a version that does not
            include container reporting.
          </p>
        ) : containers.length === 0 ? (
          <p style={{ color: colors.textMuted, fontSize: '0.875rem' }}>
            No managed containers reported in the latest heartbeat.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                {['Service', 'Status', 'Uptime', 'Image'].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      color: colors.textMuted,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {containers.map((c) => (
                <tr key={c.name}>
                  <td style={{ padding: '8px', borderBottom: `1px solid ${colors.border}` }}>
                    <strong>{c.name}</strong>
                  </td>
                  <td style={{ padding: '8px', borderBottom: `1px solid ${colors.border}` }}>
                    <StatusBadge status={containerStatusBadge(c.status)} label={c.status} />
                  </td>
                  <td style={{ padding: '8px', borderBottom: `1px solid ${colors.border}`, color: colors.textMuted }}>
                    {relativeUptime(c.started)}
                  </td>
                  <td
                    style={{
                      padding: '8px',
                      borderBottom: `1px solid ${colors.border}`,
                      color: colors.textMuted,
                      fontFamily: 'monospace',
                      fontSize: '0.8125rem',
                    }}
                  >
                    {c.image}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {env.notes && (
        <div style={{ marginTop: '1rem', color: colors.textMuted, whiteSpace: 'pre-wrap', maxWidth: '900px' }}>
          {env.notes}
        </div>
      )}

      {/* Recent Events card */}
      <div
        style={{
          marginTop: '1rem',
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '1rem',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: '0.75rem', color: colors.textHeading }}>Recent Events</div>
        {recentEvents.length === 0 ? (
          <div style={{ color: colors.textMuted, fontSize: '0.8125rem' }}>No operational events recorded yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {recentEvents.map((ev) => (
              <div key={ev.id}>
                <div
                  onClick={() => setExpandedEvent(expandedEvent === ev.id ? null : ev.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.375rem 0.5rem',
                    background: colors.bgMuted,
                    borderRadius: expandedEvent === ev.id ? '4px 4px 0 0' : '4px',
                    cursor: ev.details ? 'pointer' : 'default',
                    fontSize: '0.8125rem',
                  }}
                >
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: ev.severity === 'error' ? '#dc2626' : ev.severity === 'warn' ? '#f59e0b' : '#16a34a',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: colors.textMuted, fontSize: '0.75rem', minWidth: '55px' }}>
                    {new Date(ev.occurredAt).toLocaleTimeString()}
                  </span>
                  <StatusBadge
                    status={ev.source === 'provisioning' ? 'in_progress' : 'active'}
                    label={ev.source.replace('_', ' ')}
                  />
                  <span style={{ flex: 1, color: colors.text }}>{ev.summary}</span>
                  {ev.details && (
                    <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                      {expandedEvent === ev.id ? 'Hide' : 'Details'}
                    </span>
                  )}
                </div>
                {expandedEvent === ev.id && ev.details && (
                  <pre
                    style={{
                      margin: 0,
                      padding: '0.5rem',
                      background: '#1e293b',
                      color: '#e2e8f0',
                      borderRadius: '0 0 4px 4px',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      maxHeight: '200px',
                      overflow: 'auto',
                    }}
                  >
                    {JSON.stringify(ev.details, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Errors card */}
      <div
        style={{
          marginTop: '0.75rem',
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '1rem',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: '0.75rem', color: colors.textHeading }}>Recent Errors</div>
        {recentErrors.length === 0 ? (
          <div style={{ color: colors.textMuted, fontSize: '0.8125rem' }}>
            No error-level log entries in the last 24h.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {recentErrors.map((e) => (
              <div
                key={e.id}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '0.5rem',
                  padding: '0.375rem 0.5rem',
                  background: '#fef2f2',
                  borderRadius: '4px',
                  fontSize: '0.8125rem',
                }}
              >
                <span style={{ color: colors.textMuted, fontSize: '0.75rem', minWidth: '55px' }}>
                  {new Date(e.ts).toLocaleTimeString()}
                </span>
                <span style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: '0.75rem' }}>{e.container}</span>
                <span style={{ color: '#dc2626', flex: 1 }}>{e.message}</span>
              </div>
            ))}
          </div>
        )}
        {id && (
          <div style={{ marginTop: '0.5rem' }}>
            <button
              type="button"
              onClick={() => navigate(`/fleet/logs?environmentIds=${id}&level=error`)}
              style={{ ...btnSecondary, fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              View all logs
            </button>
          </div>
        )}
      </div>

      <div style={{ marginTop: '1rem' }}>
        <button type="button" onClick={() => navigate('/fleet')} style={btnPrimary}>
          Back to fleet
        </button>
      </div>

      <Modal
        isOpen={showArchiveModal}
        onClose={() => {
          setShowArchiveModal(false)
          setRetainSecrets(true)
        }}
        title="Archive Environment"
        width="500px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ margin: 0, fontSize: '0.9375rem', color: colors.text }}>
            This environment will be removed from active fleet views. Heartbeats will stop being tracked.
          </p>

          <div>
            <div
              style={{
                fontSize: '0.8125rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: colors.textMuted,
                marginBottom: '0.5rem',
              }}
            >
              SSH Keys &amp; Backup Secrets
            </div>
            {retainSecrets ? (
              <div
                style={{
                  padding: '0.75rem',
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  color: '#1e40af',
                }}
              >
                SSH deploy keys and backup encryption keys will be retained in the vault for future data recovery.
              </div>
            ) : (
              <div
                style={{
                  padding: '0.75rem',
                  background: '#fef2f2',
                  border: `1px solid ${colors.borderError}`,
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  color: colors.danger,
                }}
              >
                SSH deploy keys will be revoked and deleted. If backup data was encrypted with the DEK, it becomes
                unrecoverable. This is irreversible.
              </div>
            )}
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              fontSize: '0.9375rem',
              color: colors.text,
            }}
          >
            <input type="checkbox" checked={!retainSecrets} onChange={(e) => setRetainSecrets(!e.target.checked)} />
            Delete SSH keys and secrets (irreversible)
          </label>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
            <button
              type="button"
              onClick={() => {
                setShowArchiveModal(false)
                setRetainSecrets(true)
              }}
              disabled={archiving}
              style={btnSecondary}
            >
              Cancel
            </button>
            <button type="button" onClick={handleArchive} disabled={archiving} style={btnDanger}>
              {archiving ? 'Archiving...' : 'Archive'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function Field({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: '12px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: '15px', color: colors.textHeading, marginTop: '4px' }}>{children}</div>
    </div>
  )
}
